// ─── Migration note ────────────────────────────────────────────────────────
// Run in Supabase SQL editor before deploying notes save feature:
//   ALTER TABLE retailers ADD COLUMN IF NOT EXISTS notes TEXT;
// ───────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useMemo, useCallback, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase/client";
import { RETAILER_IMPERSONATE_KEY, RETAILER_IMPERSONATE_EVENT } from "@/contexts/AppContext";
import { arDisplayStatus } from "@/lib/assetStatus";

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */

const FOUNDING_SLOTS = 250;

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

interface Retailer {
  id: string;
  store_name: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  /** Preferred address line — DB column: address */
  address: string | null;
  /** Legacy address field */
  street_address: string | null;
  city: string | null;
  /** Primary region abbreviation — DB column: state */
  state: string | null;
  /** Legacy region field */
  province: string | null;
  country: string | null;
  founding_country: string | null;
  /** Preferred postal code */
  zip_code: string | null;
  /** Legacy postal code */
  postal_code: string | null;
  /** Legacy founding flag */
  founding_member: boolean | null;
  /** Canonical founding flag */
  is_founding_member: boolean | null;
  /** Canonical founding slot number */
  founding_member_number: number | null;
  /** Legacy founding slot number */
  founding_cohort_number: number | null;
  /** String status field: 'active' | 'pending' | 'inactive' */
  status: string | null;
  /** Tier field: 'founding' | 'standard' */
  tier: string | null;
  /** Legacy active flag */
  is_active: boolean | null;
  /** Rate-locked price in cents */
  rate_locked_price_cents: number | null;
  founding_cohort: string | null;
  product_count: number | null;
  hubspot_company_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  notes: string | null;
  is_demo: boolean | null;
  /** Geocoding — populated by the update-retailer-address edge function. */
  latitude: number | null;
  longitude: number | null;
  /** 'pending' | 'ok' | 'failed' | 'manual'; null = never attempted */
  geocode_status: string | null;
  /** Provider location_type, lowercased. Below rooftop = a centroid. */
  geocode_precision: string | null;
  geocoded_at: string | null;
  /** Store-pairing code consumers type in the mobile app. Null until issued. */
  pairing_code: string | null;
  created_at: string;
  updated_at: string | null;
}

interface PipelineProduct {
  id: string;
  asset_status: string | null;
  glb_pbr_url: string | null;
}

type SortKey      = "created_at" | "store_name" | "founding_member_number" | "product_count";
type StatusFilter = "all" | "active" | "pending" | "inactive";
type TierFilter   = "all" | "founding" | "standard";
type CountryFilter = "all" | "Canada" | "United States";

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

function relativeDate(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30)  return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function getStatus(r: Retailer): "active" | "pending" | "inactive" {
  if (r.status === "active"   || r.is_active === true)  return "active";
  if (r.status === "inactive" || r.is_active === false) return "inactive";
  if (r.status === "pending")                           return "pending";
  return "pending";
}

function isFounding(r: Retailer): boolean {
  return !!(r.is_founding_member || r.founding_member || r.tier === "founding");
}

function getFoundingNumber(r: Retailer): number | null {
  return r.founding_member_number ?? r.founding_cohort_number ?? null;
}

/* ------------------------------------------------------------------ */
/* Badge components                                                     */
/* ------------------------------------------------------------------ */

function TierBadge({ founding }: { founding: boolean }) {
  if (founding) {
    return (
      <span className="inline-flex items-center rounded-full border border-teal-700 bg-teal-900/30 px-2 py-0.5 text-xs font-medium text-teal-400">
        Founding
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-gray-700 bg-gray-800/60 px-2 py-0.5 text-xs font-medium text-gray-400">
      Standard
    </span>
  );
}

const STATUS_META: Record<string, { label: string; classes: string }> = {
  active:   { label: "Active",   classes: "border-green-700 bg-green-900/30 text-green-400" },
  pending:  { label: "Pending",  classes: "border-amber-700 bg-amber-900/30 text-amber-400" },
  inactive: { label: "Inactive", classes: "border-gray-700 bg-gray-800/60 text-gray-500"   },
};

function StatusBadge({ status }: { status: "active" | "pending" | "inactive" }) {
  const meta = STATUS_META[status];
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${meta.classes}`}>
      {meta.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Stat chip (mirrors AdminProducts pattern)                           */
/* ------------------------------------------------------------------ */

function StatChip({
  label,
  value,
  accent,
  dim,
}: {
  label: string;
  value: number | string;
  accent?: string;
  dim?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border border-gray-800 bg-gray-900/60 px-4 py-2 text-center min-w-[80px] ${
        dim ? "opacity-60" : ""
      }`}
    >
      <p className={`text-xl font-semibold tabular-nums ${accent ?? "text-white"}`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Panel sub-components                                                 */
/* ------------------------------------------------------------------ */

function PanelSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-b border-gray-800 p-5">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</h3>
      {children}
    </div>
  );
}

function PanelRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="shrink-0 text-gray-500">{label}</dt>
      <dd className="min-w-0 flex-1 text-right">{children}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pipeline stats helper                                                */
/* ------------------------------------------------------------------ */

function getPipelineStats(
  products: PipelineProduct[] | null,
): { total: number; arReady: number; pending: number } | null {
  if (!products) return null;
  const total   = products.length;
  // Normalised, not a literal `asset_status` comparison: the approved path
  // writes `ready`, so counting only `complete` reported every published
  // product as not-ready. See src/lib/assetStatus.ts.
  const arReady = products.filter(
    (p) => !!p.glb_pbr_url && arDisplayStatus(p) === "ready",
  ).length;
  const pending = products.filter((p) => {
    const s = arDisplayStatus(p);
    return !p.glb_pbr_url || s === "pending" || s === "processing";
  }).length;
  return { total, arReady, pending };
}

/* ------------------------------------------------------------------ */
/* Main component                                                       */
/* ------------------------------------------------------------------ */

export default function AdminRetailers() {
  const { toast } = useToast();
  const navigate = useNavigate();

  // ── Data ─────────────────────────────────────────────────────────────
  const [retailers,  setRetailers]  = useState<Retailer[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // ── Filters ──────────────────────────────────────────────────────────
  const [search,          setSearch]          = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter,    setStatusFilter]    = useState<StatusFilter>("all");
  const [tierFilter,      setTierFilter]      = useState<TierFilter>("all");
  const [countryFilter,   setCountryFilter]   = useState<CountryFilter>("all");
  const [sortKey,         setSortKey]         = useState<SortKey>("created_at");
  const [sortAsc,         setSortAsc]         = useState(false);

  // ── Detail panel ─────────────────────────────────────────────────────
  const [selectedRetailer,     setSelectedRetailer]     = useState<Retailer | null>(null);
  const [panelProducts,        setPanelProducts]        = useState<PipelineProduct[] | null>(null);
  const [panelProductsLoading, setPanelProductsLoading] = useState(false);
  const [pairingBusy,          setPairingBusy]          = useState(false);
  const [accessBusy,           setAccessBusy]           = useState(false);
  const [notesDraft,           setNotesDraft]           = useState("");
  const [notesSaving,          setNotesSaving]          = useState(false);
  const [statusDraft,          setStatusDraft]          = useState("active");
  const [statusUpdating,       setStatusUpdating]       = useState(false);

  // ── Progress bar animation ───────────────────────────────────────────
  const [progressFill, setProgressFill] = useState(0);

  // ── Debounce search 300 ms ───────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // ── Fetch ────────────────────────────────────────────────────────────
  const fetchRetailers = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const { data, error } = await supabase
        .from("retailers")
        .select(
          "id, store_name, email, phone, website, " +
          "address, street_address, city, state, province, country, founding_country, " +
          "zip_code, postal_code, " +
          "founding_member, is_founding_member, founding_member_number, founding_cohort_number, " +
          "status, tier, is_active, rate_locked_price_cents, founding_cohort, " +
          "product_count, hubspot_company_id, contact_name, contact_email, notes, is_demo, " +
          "latitude, longitude, geocode_status, geocode_precision, geocoded_at, pairing_code, " +
          "created_at, updated_at",
        )
        .order("created_at", { ascending: false });
      if (error) throw error;
      setRetailers((data as unknown as Retailer[]) ?? []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[AdminRetailers] fetch error:", msg, err);
      setFetchError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRetailers(); }, [fetchRetailers]);

  // Animate progress bar once load completes
  useEffect(() => {
    if (loading) { setProgressFill(0); return; }
    const foundingCount = retailers.filter(isFounding).length;
    const pct = Math.min(100, (foundingCount / FOUNDING_SLOTS) * 100);
    const t = setTimeout(() => setProgressFill(pct), 80);
    return () => clearTimeout(t);
  }, [loading, retailers]);

  // ── Derived / filtered list ──────────────────────────────────────────
  const filtered = useMemo(() => {
    let rows = [...retailers];

    if (debouncedSearch.trim()) {
      const q = debouncedSearch.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          r.store_name.toLowerCase().includes(q) ||
          (r.city ?? "").toLowerCase().includes(q) ||
          (r.state ?? r.province ?? "").toLowerCase().includes(q) ||
          (r.email ?? "").toLowerCase().includes(q),
      );
    }

    if (statusFilter !== "all") {
      rows = rows.filter((r) => getStatus(r) === statusFilter);
    }

    if (tierFilter !== "all") {
      rows = rows.filter((r) =>
        tierFilter === "founding" ? isFounding(r) : !isFounding(r),
      );
    }

    if (countryFilter !== "all") {
      rows = rows.filter((r) => {
        const c = (r.founding_country ?? r.country ?? "").toLowerCase();
        return c.includes(countryFilter.toLowerCase());
      });
    }

    rows.sort((a, b) => {
      let cmp = 0;
      if      (sortKey === "store_name")            cmp = a.store_name.localeCompare(b.store_name);
      else if (sortKey === "founding_member_number") cmp = (getFoundingNumber(a) ?? 9999) - (getFoundingNumber(b) ?? 9999);
      else if (sortKey === "product_count")          cmp = (a.product_count ?? 0) - (b.product_count ?? 0);
      else                                           cmp = a.created_at.localeCompare(b.created_at);
      return sortAsc ? cmp : -cmp;
    });

    return rows;
  }, [retailers, debouncedSearch, statusFilter, tierFilter, countryFilter, sortKey, sortAsc]);

  // Stats always reflect global (unfiltered) set
  const stats = useMemo(() => {
    const founding = retailers.filter(isFounding).length;
    const active   = retailers.filter((r) => getStatus(r) === "active").length;
    const pending  = retailers.filter((r) => getStatus(r) === "pending").length;
    return { total: retailers.length, founding, active, pending };
  }, [retailers]);

  const hasActiveFilters = !!(
    debouncedSearch || statusFilter !== "all" || tierFilter !== "all" || countryFilter !== "all"
  );

  // ── Open detail panel ────────────────────────────────────────────────
  async function openPanel(r: Retailer) {
    setSelectedRetailer(r);
    setNotesDraft(r.notes ?? "");
    setStatusDraft(getStatus(r));
    setPanelProducts(null);
    setPanelProductsLoading(true);
    try {
      const { data, error } = await supabase
        .from("products")
        .select("id, asset_status, glb_pbr_url")
        .eq("retailer_id", r.id);
      if (!error) setPanelProducts((data as unknown as PipelineProduct[]) ?? []);
    } catch {
      // Pipeline stats are non-critical — leave panelProducts null
    } finally {
      setPanelProductsLoading(false);
    }
  }

  function closePanel() {
    setSelectedRetailer(null);
    setPanelProducts(null);
  }

  // ── Notes save ───────────────────────────────────────────────────────
  async function saveNotes() {
    if (!selectedRetailer) return;
    setNotesSaving(true);
    try {
      const { error } = await supabase
        .from("retailers")
        .update({ notes: notesDraft })
        .eq("id", selectedRetailer.id);
      if (error) throw error;
      setRetailers((prev) =>
        prev.map((r) => (r.id === selectedRetailer.id ? { ...r, notes: notesDraft } : r)),
      );
      setSelectedRetailer((prev) => (prev ? { ...prev, notes: notesDraft } : prev));
      toast({ title: "Notes saved" });
    } catch (err) {
      // Graceful fail — notes column may not exist yet
      console.warn("[AdminRetailers] notes save failed:", err);
      toast({
        title: "Could not save notes",
        description: "Run: ALTER TABLE retailers ADD COLUMN IF NOT EXISTS notes TEXT;",
      });
    } finally {
      setNotesSaving(false);
    }
  }

  // ── Pairing code (re)generate ────────────────────────────────────────
  // regenerate_retailer_pairing_code is admin-only server-side and replaces any existing
  // code. Pairings already created stay valid until they expire; only new pairing attempts
  // need the new code, so tell the retailer to give customers the new one.
  async function regeneratePairingCode() {
    if (!selectedRetailer) return;
    const had = !!selectedRetailer.pairing_code;
    if (had && !window.confirm(
      `Replace the pairing code for ${selectedRetailer.store_name}? The current code stops working for new customers.`,
    )) return;
    setPairingBusy(true);
    try {
      const { data, error } = await supabase.rpc("regenerate_retailer_pairing_code", {
        p_retailer_id: selectedRetailer.id,
      });
      if (error) throw error;
      const code = data as unknown as string;
      setRetailers((prev) =>
        prev.map((r) => (r.id === selectedRetailer.id ? { ...r, pairing_code: code } : r)),
      );
      setSelectedRetailer((prev) => (prev ? { ...prev, pairing_code: code } : prev));
      toast({ title: had ? "Pairing code replaced" : "Pairing code issued" });
    } catch (err) {
      console.error("[AdminRetailers] pairing code regenerate failed:", err);
      toast({ title: "Could not generate pairing code", description: err instanceof Error ? err.message : undefined });
    } finally {
      setPairingBusy(false);
    }
  }

  // ── Portal access: activate without payment / suspend ───────────────
  // Both go through set-retailer-access (admin only, audited). Nothing suspends automatically:
  // late payment is handled here by hand, per the Order Form's late-payment clause.
  async function changeAccess(action: "activate" | "suspend") {
    if (!selectedRetailer) return;
    const name = selectedRetailer.store_name;
    const reason = window.prompt(
      action === "suspend"
        ? `Suspend portal access for ${name}?\n\nReason, kept in the audit trail (e.g. "invoice 30 days overdue"):`
        : `Activate portal access for ${name} without payment?\n\nReason, kept in the audit trail (e.g. "pilot", "demo account"):`,
      "",
    );
    if (reason === null) return; // cancelled
    setAccessBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("set-retailer-access", {
        body: { retailer_id: selectedRetailer.id, action, reason: reason.trim() || undefined },
      });
      if (error || data?.error) throw new Error(error?.message ?? data?.error);
      const on = action === "activate";
      const patch = { status: on ? "active" : "inactive", is_active: on };
      setRetailers((prev) => prev.map((r) => (r.id === selectedRetailer.id ? { ...r, ...patch } : r)));
      setSelectedRetailer((prev) => (prev ? { ...prev, ...patch } : prev));
      setStatusDraft(on ? "active" : "inactive");
      toast({ title: on ? "Portal access activated" : "Portal access suspended" });
    } catch (err) {
      console.error("[AdminRetailers] access change failed:", err);
      toast({ title: "Could not change access", description: err instanceof Error ? err.message : undefined });
    } finally {
      setAccessBusy(false);
    }
  }

  // ── Status update ────────────────────────────────────────────────────
  async function updateStatus() {
    if (!selectedRetailer) return;
    if (!window.confirm(`Change status to "${statusDraft}" for ${selectedRetailer.store_name}?`)) return;
    setStatusUpdating(true);
    try {
      const { error } = await supabase
        .from("retailers")
        .update({ status: statusDraft })
        .eq("id", selectedRetailer.id);
      if (error) throw error;
      setRetailers((prev) =>
        prev.map((r) => (r.id === selectedRetailer.id ? { ...r, status: statusDraft } : r)),
      );
      setSelectedRetailer((prev) => (prev ? { ...prev, status: statusDraft } : prev));
      toast({ title: `Status updated to ${statusDraft}` });
    } catch (err) {
      console.error("[AdminRetailers] status update failed:", err);
      toast({
        title: "Status update failed",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setStatusUpdating(false);
    }
  }

  // ── Sort toggle ──────────────────────────────────────────────────────
  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortAsc((a) => !a);
    else { setSortKey(key); setSortAsc(true); }
  }

  function SortBtn({ col, label }: { col: SortKey; label: string }) {
    const active = sortKey === col;
    return (
      <button
        onClick={() => toggleSort(col)}
        className={`flex items-center gap-1 text-left font-medium transition ${
          active ? "text-teal-400" : "text-gray-400 hover:text-gray-200"
        }`}
      >
        {label}
        <span className="text-xs">{active ? (sortAsc ? "↑" : "↓") : ""}</span>
      </button>
    );
  }

  const inputStyle = { background: "#111827", color: "#f9fafb" };
  const inputCls   = "rounded-lg border border-gray-700 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none";

  /* ── Render ─────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Retailers</h1>
          <p className="mt-0.5 text-sm text-gray-400">Manage retail partners and founding members</p>
        </div>
      </div>

      {/* ── Stats bar ── */}
      <div className="flex flex-wrap gap-3">
        <StatChip label="Total"                           value={stats.total}    />
        <StatChip label={`Founding / ${FOUNDING_SLOTS}`} value={stats.founding} accent="text-teal-400" />
        <StatChip label="Active"                          value={stats.active}   accent="text-green-400" />
        <StatChip label="Pending"                         value={stats.pending}  accent="text-amber-400"
                  dim={stats.pending === 0} />
      </div>

      {/* ── Founding member progress bar ── */}
      <div className="rounded-xl border border-gray-800 bg-gray-900/60 px-5 py-4">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-medium text-teal-400">Founding Members</span>
          <span className="tabular-nums text-gray-400">
            {stats.founding} / {FOUNDING_SLOTS} spots filled
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-800">
          <div
            className="h-full rounded-full bg-teal-500 transition-all duration-700 ease-out"
            style={{ width: `${progressFill}%` }}
          />
        </div>
        {stats.founding >= FOUNDING_SLOTS && (
          <p className="mt-1.5 text-xs text-teal-400">All founding slots filled 🎉</p>
        )}
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-wrap gap-3">
        <div className="flex flex-col gap-1">
          <input
            type="text"
            placeholder="Search store name, city, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={inputStyle}
            className={`${inputCls} w-full sm:w-72`}
          />
          {debouncedSearch && (
            <p className="pl-1 text-xs text-gray-600">
              {filtered.length} result{filtered.length !== 1 ? "s" : ""} for &ldquo;{debouncedSearch}&rdquo;
            </p>
          )}
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          style={inputStyle}
          className={inputCls}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="inactive">Inactive</option>
        </select>

        <select
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value as TierFilter)}
          style={inputStyle}
          className={inputCls}
        >
          <option value="all">All tiers</option>
          <option value="founding">Founding</option>
          <option value="standard">Standard</option>
        </select>

        <select
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value as CountryFilter)}
          style={inputStyle}
          className={inputCls}
        >
          <option value="all">All countries</option>
          <option value="Canada">Canada</option>
          <option value="United States">United States</option>
        </select>

        <select
          value={sortKey}
          onChange={(e) => { setSortKey(e.target.value as SortKey); setSortAsc(true); }}
          style={inputStyle}
          className={inputCls}
        >
          <option value="created_at">Sort: Newest</option>
          <option value="store_name">Sort: Name A–Z</option>
          <option value="founding_member_number">Sort: Founding #</option>
          <option value="product_count">Sort: Most Products</option>
        </select>

        {hasActiveFilters && (
          <button
            onClick={() => {
              setSearch("");
              setStatusFilter("all");
              setTierFilter("all");
              setCountryFilter("all");
            }}
            className="text-xs text-gray-500 hover:text-gray-300 underline underline-offset-2 transition"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* ── Fetch error (inline, dismissible) ── */}
      {fetchError && !loading && (
        <div className="flex items-start gap-3 rounded-lg border border-red-800 bg-red-950/40 px-4 py-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-red-400">Failed to load retailers</p>
            <p className="mt-0.5 truncate text-xs text-red-500">{fetchError}</p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <button
              onClick={fetchRetailers}
              className="text-xs text-red-400 underline hover:text-red-300"
            >
              Retry
            </button>
            <button
              onClick={() => setFetchError(null)}
              aria-label="Dismiss error"
              className="text-gray-500 hover:text-gray-300"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div className="rounded-xl border border-gray-800 py-16 text-center text-sm text-gray-500">
          Loading retailers…
        </div>
      ) : filtered.length === 0 && retailers.length === 0 ? (
        <div className="rounded-xl border border-gray-800 py-16 text-center text-sm text-gray-500">
          No retailers yet.
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-gray-800 py-12 text-center text-sm text-gray-500">
          No retailers match your filters.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/60">
              <tr className="border-b border-gray-800">
                <th className="w-12 px-4 py-3 text-left">
                  <SortBtn col="founding_member_number" label="#" />
                </th>
                <th className="px-4 py-3 text-left">
                  <SortBtn col="store_name" label="Store" />
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Tier</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Status</th>
                <th className="px-4 py-3 text-left">
                  <SortBtn col="product_count" label="Products" />
                </th>
                <th className="px-4 py-3 text-left">
                  <SortBtn col="created_at" label="Member Since" />
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const fNum   = getFoundingNumber(r);
                const status = getStatus(r);
                const city   = r.city ?? "";
                const region = r.state ?? r.province ?? "";
                const loc    = [city, region].filter(Boolean).join(", ");
                return (
                  <tr
                    key={r.id}
                    onClick={() => openPanel(r)}
                    className="group cursor-pointer border-t border-gray-800 transition hover:bg-gray-900/40"
                  >
                    {/* Founding # */}
                    <td className="px-4 py-3 text-xs tabular-nums text-gray-500">
                      {isFounding(r) && fNum != null ? `#${fNum}` : "—"}
                    </td>

                    {/* Store name + city/state */}
                    <td className="max-w-[240px] px-4 py-3">
                      <span className="block truncate font-medium">{r.store_name}</span>
                      {loc && (
                        <span className="mt-0.5 block truncate text-xs text-gray-500">{loc}</span>
                      )}
                    </td>

                    {/* Tier badge + demo badge */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <TierBadge founding={isFounding(r)} />
                        {r.is_demo && (
                          <span className="inline-flex items-center rounded-full border border-amber-700/60 bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-400">
                            Demo
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Status badge */}
                    <td className="px-4 py-3">
                      <StatusBadge status={status} />
                    </td>

                    {/* Product count */}
                    <td className="px-4 py-3 text-xs tabular-nums text-gray-300">
                      {r.product_count ?? "—"}
                    </td>

                    {/* Member since */}
                    <td
                      className="whitespace-nowrap px-4 py-3 text-xs text-gray-500"
                      title={new Date(r.created_at).toLocaleString()}
                    >
                      {relativeDate(r.created_at)}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            localStorage.setItem(RETAILER_IMPERSONATE_KEY, r.id);
                            window.dispatchEvent(new Event(RETAILER_IMPERSONATE_EVENT));
                            navigate("/portal");
                          }}
                          className="rounded border border-teal-800 px-2.5 py-1 text-xs text-teal-400 opacity-0 transition-opacity hover:border-teal-600 hover:text-teal-300 group-hover:opacity-100"
                        >
                          Impersonate
                        </button>
                        <button
                          onClick={() => openPanel(r)}
                          className="rounded border border-gray-700 px-2.5 py-1 text-xs text-gray-400 opacity-0 transition-opacity hover:border-gray-500 hover:text-gray-200 group-hover:opacity-100"
                        >
                          Details
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Table footer */}
          <div className="border-t border-gray-800 px-4 py-2.5 text-xs text-gray-600">
            {hasActiveFilters
              ? `${filtered.length} of ${retailers.length} retailers`
              : `${retailers.length} retailer${retailers.length !== 1 ? "s" : ""}`}
          </div>
        </div>
      )}

      {/* ── Detail panel backdrop ── */}
      {selectedRetailer && (
        <div className="fixed inset-0 z-40 bg-black/40" onClick={closePanel} />
      )}

      {/* ── Detail panel (420 px, slides from right) ── */}
      <div
        className={`fixed right-0 top-0 z-50 h-full w-[420px] overflow-y-auto border-l border-gray-800 bg-gray-950 shadow-2xl transition-transform duration-300 ease-in-out ${
          selectedRetailer ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {selectedRetailer && (
          <div className="flex flex-col">

            {/* ── Section 1: Header ── */}
            <div className="flex items-start justify-between border-b border-gray-800 p-5">
              <div>
                <h2 className="text-lg font-semibold leading-tight">
                  {selectedRetailer.store_name}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <TierBadge founding={isFounding(selectedRetailer)} />
                  <StatusBadge status={getStatus(selectedRetailer)} />
                </div>
              </div>
              <button
                onClick={closePanel}
                className="ml-4 mt-0.5 rounded border border-gray-700 px-2.5 py-1 text-sm text-gray-400 transition hover:border-gray-500 hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            {/* ── Section 2: Contact ── */}
            <PanelSection title="Contact">
              <dl className="space-y-2 text-sm">
                {selectedRetailer.email ? (
                  <PanelRow label="Email">
                    <a
                      href={`mailto:${selectedRetailer.email}`}
                      className="truncate text-teal-400 hover:underline"
                    >
                      {selectedRetailer.email}
                    </a>
                  </PanelRow>
                ) : null}
                {selectedRetailer.phone ? (
                  <PanelRow label="Phone">
                    <a
                      href={`tel:${selectedRetailer.phone}`}
                      className="text-teal-400 hover:underline"
                    >
                      {selectedRetailer.phone}
                    </a>
                  </PanelRow>
                ) : null}
                {selectedRetailer.website ? (
                  <PanelRow label="Website">
                    <a
                      href={selectedRetailer.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-teal-400 hover:underline"
                    >
                      {selectedRetailer.website} ↗
                    </a>
                  </PanelRow>
                ) : null}
                {(selectedRetailer.address ?? selectedRetailer.street_address) ? (
                  <PanelRow label="Address">
                    <span className="text-gray-300">
                      {selectedRetailer.address ?? selectedRetailer.street_address}
                      {(selectedRetailer.zip_code ?? selectedRetailer.postal_code)
                        ? `, ${selectedRetailer.zip_code ?? selectedRetailer.postal_code}`
                        : null}
                    </span>
                  </PanelRow>
                ) : null}
                {!selectedRetailer.email &&
                  !selectedRetailer.phone &&
                  !selectedRetailer.website &&
                  !(selectedRetailer.address ?? selectedRetailer.street_address) && (
                    <p className="text-xs text-gray-600">No contact information on file.</p>
                  )}
              </dl>
            </PanelSection>

            {/* ── Section 3: Membership ── */}
            <PanelSection title="Membership">
              <dl className="space-y-2 text-sm">
                <PanelRow label="Tier">
                  <span className="text-gray-300">
                    {isFounding(selectedRetailer) ? "Founding" : "Standard"}
                  </span>
                </PanelRow>
                {getFoundingNumber(selectedRetailer) != null && (
                  <PanelRow label="Founding #">
                    <span className="text-gray-300">#{getFoundingNumber(selectedRetailer)}</span>
                  </PanelRow>
                )}
                <PanelRow label="Member since">
                  <span className="text-gray-300">{relativeDate(selectedRetailer.created_at)}</span>
                </PanelRow>
                {selectedRetailer.founding_country && (
                  <PanelRow label="Country">
                    <span className="text-gray-300">{selectedRetailer.founding_country}</span>
                  </PanelRow>
                )}
                {selectedRetailer.rate_locked_price_cents != null && (
                  <PanelRow label="Rate-locked price">
                    <span className="text-gray-300">
                      ${(selectedRetailer.rate_locked_price_cents / 100).toFixed(2)}/mo
                    </span>
                  </PanelRow>
                )}
              </dl>
            </PanelSection>

            {/* ── Section 4: Pipeline stats ── */}
            <PanelSection title="Pipeline Stats">
              {panelProductsLoading ? (
                <p className="text-xs text-gray-600">Loading…</p>
              ) : panelProducts === null ? (
                <p className="text-xs text-gray-600">No data available.</p>
              ) : (
                (() => {
                  const ps = getPipelineStats(panelProducts);
                  if (!ps) return <p className="text-xs text-gray-600">No data available.</p>;
                  return (
                    <dl className="space-y-2 text-sm">
                      <PanelRow label="Total products">
                        <span className="tabular-nums text-gray-300">{ps.total}</span>
                      </PanelRow>
                      <PanelRow label="AR Ready">
                        <span className="tabular-nums text-green-400">{ps.arReady}</span>
                      </PanelRow>
                      <PanelRow label="Pending / processing">
                        <span className="tabular-nums text-amber-400">{ps.pending}</span>
                      </PanelRow>
                    </dl>
                  );
                })()
              )}
            </PanelSection>

            {/* ── Section 5: HubSpot (only if ID set) ── */}
            {selectedRetailer.hubspot_company_id && (
              <PanelSection title="HubSpot">
                <a
                  href={`https://app.hubspot.com/contacts/${selectedRetailer.hubspot_company_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-400 transition hover:border-gray-500 hover:text-gray-200"
                >
                  Open in HubSpot ↗
                </a>
              </PanelSection>
            )}

            {/* ── Store pairing code ── */}
            <PanelSection title="Store pairing code">
              <div className="flex items-center justify-between gap-3">
                {selectedRetailer.pairing_code ? (
                  <span className="font-mono text-lg font-semibold tracking-widest text-orange-400">
                    {selectedRetailer.pairing_code}
                  </span>
                ) : (
                  <span className="text-sm text-gray-500 italic">No code issued yet.</span>
                )}
                <button
                  onClick={regeneratePairingCode}
                  disabled={pairingBusy}
                  className="rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-300 transition hover:border-gray-500 disabled:opacity-50"
                >
                  {pairingBusy ? "Working…" : selectedRetailer.pairing_code ? "Regenerate" : "Generate"}
                </button>
              </div>
            </PanelSection>

            {/* ── Portal access ── */}
            <PanelSection title="Portal access">
              {(() => {
                const active = getStatus(selectedRetailer) === "active";
                return (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="space-y-1">
                        <StatusBadge status={getStatus(selectedRetailer)} />
                        <p className="text-xs text-gray-500">
                          {active
                            ? "The retailer can use the portal."
                            : "The retailer is sent to the billing page instead of the portal."}
                        </p>
                      </div>
                      {active ? (
                        <button
                          onClick={() => changeAccess("suspend")}
                          disabled={accessBusy}
                          className="rounded-lg border border-red-900/50 bg-red-900/10 px-3 py-1.5 text-sm text-red-400 transition hover:bg-red-900/20 disabled:opacity-50"
                        >
                          {accessBusy ? "Working…" : "Suspend access"}
                        </button>
                      ) : (
                        <button
                          onClick={() => changeAccess("activate")}
                          disabled={accessBusy}
                          className="rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-200 transition hover:border-gray-500 disabled:opacity-50"
                        >
                          {accessBusy ? "Working…" : "Activate without payment"}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-600">
                      Access switches on when an agreement is invoiced. Nothing suspends automatically:
                      overdue invoices are handled here, per the Order Form's late-payment clause.
                    </p>
                  </div>
                );
              })()}
            </PanelSection>

            {/* ── Map location (geocoding) ── */}
            {/* Read-only: coordinates are written by the update-retailer-address
                edge function, not edited here. A retailer only appears on the
                mobile map when geocode_status is 'ok'. */}
            <PanelSection title="Map location">
              {(() => {
                const gs = selectedRetailer.geocode_status;
                const precision = selectedRetailer.geocode_precision;
                const hasCoords =
                  selectedRetailer.latitude != null && selectedRetailer.longitude != null;
                // Anything below rooftop/range_interpolated is a centroid — a
                // pin in the middle of a city, not at the storefront.
                const isCentroid =
                  !!precision && !["rooftop", "range_interpolated"].includes(precision);

                const badge = {
                  ok:      { label: "Geocoded",       cls: "border-teal-700 text-teal-300" },
                  failed:  { label: "Geocode failed", cls: "border-red-700 text-red-300" },
                  pending: { label: "Geocode pending", cls: "border-amber-700 text-amber-300" },
                  manual:  { label: "Set manually",   cls: "border-blue-700 text-blue-300" },
                }[gs ?? ""] ?? { label: "Never geocoded", cls: "border-gray-700 text-gray-400" };

                return (
                  <div className="space-y-2 text-sm">
                    <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs ${badge.cls}`}>
                      {badge.label}
                    </span>

                    {hasCoords ? (
                      <p className="font-mono text-xs text-gray-400">
                        {selectedRetailer.latitude}, {selectedRetailer.longitude}
                        {precision && <span className="ml-2 text-gray-500">({precision})</span>}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500">
                        No coordinates — this retailer is not shown on the mobile map.
                      </p>
                    )}

                    {gs === "failed" && (
                      <p className="text-xs text-red-400">
                        The address could not be resolved. Check the street address, city and
                        postal code, then re-save the address to retry.
                      </p>
                    )}

                    {isCentroid && (
                      <p className="text-xs text-amber-400">
                        Approximate only — this is an area centroid, not the storefront.
                        A more complete street address would improve it.
                      </p>
                    )}

                    {selectedRetailer.geocoded_at && (
                      <p className="text-xs text-gray-600">
                        Last attempt {new Date(selectedRetailer.geocoded_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                );
              })()}
            </PanelSection>

            {/* ── Section 6: Notes ── */}
            <PanelSection title="Notes">
              <textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                placeholder="Add internal notes…"
                rows={4}
                className="w-full resize-y rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:border-teal-500 focus:outline-none"
              />
              <button
                onClick={saveNotes}
                disabled={notesSaving}
                className="mt-2 rounded-lg bg-teal-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-teal-600 disabled:opacity-50"
              >
                {notesSaving ? "Saving…" : "Save notes"}
              </button>
            </PanelSection>

            {/* ── Section 7: Status change ── */}
            <PanelSection title="Change Status">
              <div className="flex items-center gap-2">
                <select
                  value={statusDraft}
                  onChange={(e) => setStatusDraft(e.target.value)}
                  style={{ background: "#111827", color: "#f9fafb" }}
                  className="flex-1 rounded-lg border border-gray-700 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none"
                >
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="inactive">Inactive</option>
                </select>
                <button
                  onClick={updateStatus}
                  disabled={statusUpdating}
                  className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-300 transition hover:bg-gray-800 disabled:opacity-50"
                >
                  {statusUpdating ? "Updating…" : "Update status"}
                </button>
              </div>
            </PanelSection>

            {/* ── Section 8: Impersonate ── */}
            <PanelSection title="Actions">
              <button
                onClick={() => {
                  localStorage.setItem(RETAILER_IMPERSONATE_KEY, selectedRetailer!.id);
                  window.dispatchEvent(new Event(RETAILER_IMPERSONATE_EVENT));
                  navigate("/portal");
                }}
                className="w-full rounded-lg border border-teal-800 px-4 py-2 text-sm text-teal-400 transition hover:border-teal-600 hover:bg-teal-950"
              >
                Impersonate this retailer
              </button>
            </PanelSection>

          </div>
        )}
      </div>
    </div>
  );
}
